import { Box, SimpleGrid, type SimpleGridProps } from "@mantine/core"
import { IconX } from "@tabler/icons-react"
import styles from "./BoxMultiSelect.module.css"
import { useKeyboardHandler } from "@utils/keyboard.utils"

export interface BoxMultiSelectOption {
	value: string
	label: string
	color?: string
}

export interface BoxMultiSelectProps {
	options: BoxMultiSelectOption[]
	value?: string[]
	defaultValue?: string[]
	onChange?: (value: string[]) => void
	onClearAll?: () => void
	showAllOption?: boolean
	allOptionLabel?: string
	allOptionColor?: string
	disabled?: boolean
	legend?: string
	fieldsetClassName?: string
	legendClassName?: string
	cols?: SimpleGridProps["cols"]
	showCloseIconWhenSelected?: boolean
}

export function BoxMultiSelect({
	options,
	value = [],
	defaultValue = [],
	onChange,
	onClearAll,
	showAllOption = false,
	allOptionLabel = "All",
	allOptionColor,
	disabled = false,
	legend,
	fieldsetClassName,
	legendClassName,
	cols = { base: 1, sm: 2 },
	showCloseIconWhenSelected = true
}: BoxMultiSelectProps) {
	const current = value.length ? value : defaultValue
	const allSelected = current.length === options.length && options.length > 0

	const emit = (next: string[]) => {
		if (next.length === 0 && onClearAll) {
			onClearAll()
			return
		}
		onChange?.(next)
	}

	const handleChange = (optionValue: string) => {
		if (disabled) return

		if (optionValue === "all") {
			emit(allSelected ? [] : options.map((o) => o.value))
			return
		}

		emit(
			current.includes(optionValue)
				? current.filter((v) => v !== optionValue)
				: [...current, optionValue]
		)
	}

	const isSelected = (optionValue: string) =>
		optionValue === "all" ? allSelected : current.includes(optionValue)

	const getSelectedStyle = (color?: string) =>
		color
			? ({
					backgroundColor: color,
					borderColor: color,
					color: "var(--mantine-color-white)"
				} as const)
			: undefined

	const renderPill = (
		optValue: string,
		label: string,
		color: string | undefined
	) => {
		const selected = isSelected(optValue)
		const keyDownHandler = useKeyboardHandler(
			() => !disabled && handleChange(optValue),
			{ debounceMs: 100 }
		)
		return (
			<Box
				key={optValue}
				className={`${styles.multiSelectBox} ${selected ? styles.selected : ""}`}
				style={selected ? getSelectedStyle(color) : undefined}
				onClick={() => (disabled ? null : handleChange(optValue))}
				onKeyDown={keyDownHandler}
				tabIndex={disabled ? -1 : 0}
				role="checkbox"
				aria-checked={selected}
				aria-disabled={disabled}
			>
				<input
					type="checkbox"
					value={optValue}
					checked={selected}
					onChange={() => (disabled ? null : handleChange(optValue))}
					className={styles.hiddenInput}
				/>
				{label}
				{selected && showCloseIconWhenSelected && (
					<span className={styles.closeIcon} aria-hidden="true">
						<IconX size={14} stroke={2.5} />
					</span>
				)}
			</Box>
		)
	}

	const grid = (
		<SimpleGrid cols={cols} spacing={8} verticalSpacing={8} role="group">
			{showAllOption && renderPill("all", allOptionLabel, allOptionColor)}
			{options.map((opt) => renderPill(opt.value, opt.label, opt.color))}
		</SimpleGrid>
	)

	if (legend) {
		return (
			<fieldset className={`${styles.fieldset} ${fieldsetClassName || ""}`}>
				<legend className={`${styles.legend} ${legendClassName || ""}`}>
					{legend}
				</legend>
				{grid}
			</fieldset>
		)
	}

	return grid
}

export default BoxMultiSelect
